import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useWorkflowNotifications } from '../utils/notifications';
import * as sessionService from '../services/sessionService';
import { WorkflowBuilder } from '../components/sessions/WorkflowBuilder';
import { WorkflowComponent, WorkflowMode } from '../components/workflows/WorkflowComponent';
import { WorkflowEventLog, logWorkflowEvent } from '../components/workflows/WorkflowEventLog';
import RelatedExecutionsView from '../components/workflows/RelatedExecutionsView';
import { WorkflowGrid } from '../components/workflows/WorkflowGrid';
import { WorkflowTable } from '../components/workflows/WorkflowTable';
import { WorkflowCommandBar } from '../components/workflows/WorkflowCommandBar';
import { useWorkflowSortFilter } from '../components/workflows/hooks/useWorkflowSortFilter';
import * as workflowExecutionService from '../services/workflowExecutionService';
import { DeleteConfirmationModal } from '../components/common/DeleteConfirmationModal';

const Workflows = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const workflowNotifications = useWorkflowNotifications();
  
  const [workflows, setWorkflows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'editor' | 'workflow_replay'>('editor');
  const [workflowToEdit, setWorkflowToEdit] = useState(null);
  const [workflowExecutions, setWorkflowExecutions] = useState<Record<string, any[]>>({});
  const [workflowMetrics, setWorkflowMetrics] = useState<Record<string, any>>({});

  // Delete-confirmation modal state — pending target + in-flight flag
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // New: Sort/filter hook
  const {
    sortedWorkflows,
    sortKey,
    sortDir,
    setSortKey,
    toggleSortDir,
    searchQuery,
    setSearchQuery,
    viewMode,
    setViewMode,
    resultCount,
    hasActiveFilters,
    clearFilters
  } = useWorkflowSortFilter({
    workflows,
    metrics: workflowMetrics,
    initialSortKey: 'updated',
    initialSortDir: 'desc',
    initialViewMode: 'grid'
  });
  
  // Check if we're in new workflow mode or edit mode
  const isNewWorkflow = location.pathname.endsWith('/new');
  const isEditMode = location.pathname.match(/\/workflows\/(.+)\/edit/);
  const editWorkflowId = isEditMode ? isEditMode[1] : null;
  
  // Reusable fetcher — also called after mutations (delete, save, etc.)
  // so the grid reflects server truth rather than only optimistic local state.
  const fetchWorkflows = useCallback(async (opts?: { withSpinner?: boolean }) => {
    if (opts?.withSpinner !== false) setLoading(true);
    try {
      const data = await sessionService.getWorkflows();
      setWorkflows(data);
      setError(null);
    } catch (err) {
      setError("Failed to fetch workflows. The workflows API might not be available yet.");
      console.error(err);
      setWorkflows([]);
    } finally {
      if (opts?.withSpinner !== false) setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only fetch workflows if we're not in new workflow or edit mode
    if (!isNewWorkflow && !isEditMode) {
      fetchWorkflows();
    } else {
      // If we're in new workflow or edit mode, we don't need to load the list
      setLoading(false);
    }
  }, [isNewWorkflow, isEditMode, fetchWorkflows]);

  // State to track which workflows have had their data loaded
  const [loadedWorkflows, setLoadedWorkflows] = useState<Set<string>>(new Set());

  // Lazy loading function for workflow execution data
  const loadWorkflowData = useCallback(async (workflowId: string) => {
    if (loadedWorkflows.has(workflowId)) {
      console.log(`Data already loaded for workflow ${workflowId}`);
      return; // Already loaded
    }
    
    console.log(`Loading data for workflow ${workflowId}...`);
    
    try {
      const executions = await workflowExecutionService.getWorkflowExecutions(workflowId);
      console.log(`Loaded ${executions.length} executions for workflow ${workflowId}:`, executions);
      
      setWorkflowExecutions(prev => ({ ...prev, [workflowId]: executions }));
      setLoadedWorkflows(prev => new Set([...prev, workflowId]));
      
      // Calculate metrics regardless of execution count (even if 0)
      const totalExecutions = executions.length;
      const completedExecutions = executions.filter(e => e.status === 'completed').length;
      const successRate = totalExecutions > 0 ? (completedExecutions / totalExecutions) * 100 : 0;
      
      const avgDuration = totalExecutions > 0 ? executions.reduce((sum, exec) => {
        const duration = exec.end_time 
          ? new Date(exec.end_time).getTime() - new Date(exec.created_at).getTime()
          : 0;
        return sum + duration;
      }, 0) / executions.length : 0;
      
      const workflow = workflows.find(w => w.id === workflowId);
      
      const metrics = {
        totalExecutions,
        successRate: Math.round(successRate),
        avgDuration,
        lastExecuted: executions.length > 0 ? executions[0].created_at : null,
        totalArtifacts: executions.reduce((sum, exec) => 
          sum + (exec.phases?.reduce((phaseSum, phase) => 
            phaseSum + (phase.artifacts?.length || 0), 0) || 0), 0),
        activePersonas: workflow?.connections?.filter(conn => 
          conn.dependencyType === 'persona_assignment').length || 0
      };
      
      console.log(`Calculated metrics for workflow ${workflowId}:`, metrics);
      
      setWorkflowMetrics(prev => ({
        ...prev,
        [workflowId]: metrics
      }));
    } catch (error) {
      console.error(`Failed to load data for workflow ${workflowId}:`, error);
      // Still mark as loaded to prevent infinite retries
      setWorkflowExecutions(prev => ({ ...prev, [workflowId]: [] }));
      setWorkflowMetrics(prev => ({
        ...prev,
        [workflowId]: {
          totalExecutions: 0,
          successRate: 0,
          avgDuration: 0,
          lastExecuted: null,
          totalArtifacts: 0,
          activePersonas: 0
        }
      }));
      setLoadedWorkflows(prev => new Set([...prev, workflowId]));
    }
  }, [loadedWorkflows, workflows]);

  // Delete workflow — open the branded confirm modal; actual delete fires
  // from confirmDeleteWorkflow once the user clicks through.
  const handleDeleteWorkflow = useCallback((workflowId: string, workflowName: string) => {
    setPendingDelete({ id: workflowId, name: workflowName });
  }, []);

  const confirmDeleteWorkflow = useCallback(async () => {
    if (!pendingDelete) return;
    const { id, name } = pendingDelete;
    setIsDeleting(true);

    // Optimistic removal — restore on error.
    const prev = workflows;
    setWorkflows((cur) => cur.filter((w) => w.id !== id));
    try {
      await sessionService.deleteWorkflow(id);
      workflowNotifications.workflowSaved(`${name} (deleted)`);
      setPendingDelete(null);
      // Background refetch — reconcile any server-side changes the optimistic
      // update couldn't see (other tab edited, server-side cascade, etc.).
      fetchWorkflows({ withSpinner: false });
    } catch (err: any) {
      console.error('Failed to delete workflow', err);
      setWorkflows(prev);
      workflowNotifications.workflowSaveError(
        `Failed to delete "${name}": ${err?.response?.data?.detail || err?.message || 'unknown error'}`,
      );
      // Keep the modal open so the user can retry or cancel.
    } finally {
      setIsDeleting(false);
    }
  }, [pendingDelete, workflows, workflowNotifications, fetchWorkflows]);

  // Handle workflow creation success
  const handleWorkflowCreated = (workflow: any) => {
    console.log('📋 WORKFLOWS-PAGE-DEBUG: handleWorkflowCreated called');
    console.log('📋 WORKFLOWS-PAGE-DEBUG: Received workflow:', workflow);
    console.log('📋 WORKFLOWS-PAGE-DEBUG: workflow.name:', workflow?.name);
    console.log('📋 WORKFLOWS-PAGE-DEBUG: workflow.id:', workflow?.id);
    console.log('🎯 WORKFLOW-COMPONENT-DEBUG: handleWorkflowCreated called');
    console.log('🎯 WORKFLOW-COMPONENT-DEBUG: Received workflow:', workflow);
    console.log('🎯 WORKFLOW-COMPONENT-DEBUG: workflow.name:', workflow?.name);
    console.log('🎯 WORKFLOW-COMPONENT-DEBUG: workflow.id:', workflow?.id);
    console.log("handleWorkflowCreated called with:", workflow);
    console.log("Workflow ID in handleWorkflowCreated:", workflow.id);
    
    // Check if this is an auto-save
    if (workflow.__isAutoSave) {
      console.log('📋 WORKFLOWS-PAGE-DEBUG: This is an auto-save, updating local state only');
      console.log('🎯 WORKFLOW-COMPONENT-DEBUG: This is an auto-save, updating local state only');
      console.log("This is an auto-save, updating local state only");
      setWorkflowToEdit(workflow);
      return;
    }
    
    // Validate the workflow data
    if (!workflow) {
      console.error('No workflow data received');
      logWorkflowEvent('error', 'No workflow data received');
      workflowNotifications.workflowSaveError('No workflow data received from server');
      return;
    }
    
    // Check for ID in different places
    let workflowId = workflow.id;
    
    // If no ID directly on workflow, check if it's in a response property
    if (!workflowId && workflow.response && workflow.response.id) {
      workflowId = workflow.response.id;
      console.log("Found ID in workflow.response:", workflowId);
    }
    
    if (!workflowId) {
      console.error('Workflow data missing ID:', workflow);
      logWorkflowEvent('error', 'Workflow data missing ID', workflow);
      workflowNotifications.workflowSaveError('Workflow data missing ID');
      return;
    }
    
    if (!workflow.name) {
      console.warn('Workflow saved without a name:', workflowId);
      logWorkflowEvent('warning', 'Workflow saved without a name', { id: workflowId });
      // This is not a critical error, just a warning
    }
    
    // For explicit saves, always navigate to the view page
    console.log('Navigating to workflow view after explicit save:', workflowId);
    logWorkflowEvent('info', `Navigating to workflow view: ${workflowId}`);
    
    // Force navigation after a short delay to ensure save completes
    setTimeout(() => {
      navigate(`/workflows/${workflowId}`);
    }, 100);
  };
  
  useEffect(() => {
    if (isNewWorkflow) {
      // Reset the workflow to edit when creating a new workflow
      setWorkflowToEdit(null);
    } else if (editWorkflowId) {
      const fetchWorkflowToEdit = async () => {
        try {
          setLoading(true);
          const data = await sessionService.getWorkflow(editWorkflowId);
          setWorkflowToEdit(data);
        } catch (err) {
          setError("Failed to fetch workflow to edit");
          console.error(err);
        } finally {
          setLoading(false);
        }
      };
      
      fetchWorkflowToEdit();
    }
  }, [isNewWorkflow, editWorkflowId]);
  
  // If we're in new workflow or edit mode, show the workflow builder
  if (isNewWorkflow || isEditMode) {
    return (
      <div className="h-screen flex flex-col overflow-hidden">
        {/* Only show tabs if we're in edit mode with a valid ID */}
        {isEditMode && editWorkflowId && (
          <div className="border-b border-gray-200 dark:border-gray-700 px-4">
            <ul className="flex flex-wrap -mb-px text-sm font-medium text-center">
              <li className="mr-2">
                <button 
                  className={`inline-block p-3 border-b-2 rounded-t-lg ${
                    activeTab === 'editor' 
                      ? `${darkMode ? 'text-blue-500 border-blue-500' : 'text-blue-600 border-blue-600'}`
                      : `${darkMode ? 'border-transparent hover:text-gray-300 hover:border-gray-300' : 'border-transparent hover:text-gray-600 hover:border-gray-300'}`
                  }`}
                  onClick={() => setActiveTab('editor')}
                >
                  Editor
                </button>
              </li>
              <li className="mr-2">
                <button 
                  className={`inline-block p-3 border-b-2 rounded-t-lg ${
                    activeTab === 'workflow_replay' 
                      ? `${darkMode ? 'text-blue-500 border-blue-500' : 'text-blue-600 border-blue-600'}`
                      : `${darkMode ? 'border-transparent hover:text-gray-300 hover:border-gray-300' : 'border-transparent hover:text-gray-600 hover:border-gray-300'}`
                  }`}
                  onClick={() => setActiveTab('workflow_replay')}
                >
                  Workflow Replay
                </button>
              </li>
            </ul>
          </div>
        )}
        
        {/* Show appropriate content based on mode and tab - full remaining space */}
        <div className="flex-1 overflow-hidden">
          {isEditMode && editWorkflowId ? (
            activeTab === 'editor' ? (
              <WorkflowComponent 
                key={editWorkflowId}
                mode={WorkflowMode.EDIT}
                workflowId={editWorkflowId}
                initialData={workflowToEdit}
                onSave={handleWorkflowCreated}
                height="100%"
              />
            ) : (
              <div className="p-4 h-full overflow-auto">
                <RelatedExecutionsView
                  workflowId={editWorkflowId}
                  darkMode={darkMode}
                />
              </div>
            )
          ) : (
            // New workflow mode - full screen editor
            <WorkflowComponent 
              key="new-workflow"
              mode={WorkflowMode.EDIT}
              initialData={null}
              onSave={handleWorkflowCreated}
            />
          )}
        </div>
      </div>
    );
  }
  
  if (loading) return <div className="p-4">Loading workflows...</div>;
  if (error) return <div className="p-4 text-red-600">Error: {error}</div>;
  
  return (
    <div className="p-4">
      {/* Add the event log */}
      <WorkflowEventLog />
      
      {/* New Command Bar with tinted glass styling */}
      <WorkflowCommandBar
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        sortKey={sortKey}
        onSortKeyChange={setSortKey}
        sortDir={sortDir}
        onToggleSortDir={toggleSortDir}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        resultCount={resultCount}
        totalCount={workflows.length}
        onCreateClick={() => navigate(`/workflows/new?t=${Date.now()}`)}
        isLoading={loading}
      />
      
      {/* Content Area - Grid or Table */}
      {sortedWorkflows.length === 0 ? (
        <div className={`
          p-12 text-center rounded-lg border-2 border-dashed
          ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}
        `}>
          <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {hasActiveFilters ? 'No workflows match your filters' : 'No workflows found'}
          </p>
          <p className={`mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {hasActiveFilters 
              ? 'Try adjusting your search or filters' 
              : 'Create your first workflow to get started'
            }
          </p>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Clear Filters
            </button>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        <WorkflowGrid
          workflows={sortedWorkflows}
          executions={workflowExecutions}
          metrics={workflowMetrics}
          onWorkflowClick={(workflowId) => navigate(`/workflows/${workflowId}`)}
          onWorkflowHover={loadWorkflowData}
          onWorkflowDelete={handleDeleteWorkflow}
        />
      ) : (
        <WorkflowTable
          workflows={sortedWorkflows}
          executions={workflowExecutions}
          metrics={workflowMetrics}
          onWorkflowClick={(workflowId) => navigate(`/workflows/${workflowId}`)}
          onWorkflowHover={loadWorkflowData}
          onWorkflowDelete={handleDeleteWorkflow}
          sortKey={sortKey}
          sortDir={sortDir}
          onSortKeyChange={setSortKey}
          onToggleSortDir={toggleSortDir}
        />
      )}

      {/* Branded delete-confirmation modal — shared across both views. */}
      <DeleteConfirmationModal
        isOpen={pendingDelete !== null}
        onClose={() => { if (!isDeleting) setPendingDelete(null); }}
        onConfirm={confirmDeleteWorkflow}
        title="Delete workflow?"
        itemType="workflow"
        itemName={pendingDelete?.name || ''}
        message={
          pendingDelete
            ? `Delete workflow "${pendingDelete.name}"? This removes the template only — existing execution history is preserved. The action cannot be undone.`
            : undefined
        }
        dangerLevel="medium"
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default Workflows;
